/**
 * funnelLogic — the arithmetic behind the Funnel tab, lifted out of the
 * component so it can be tested without a DOM or an api mock.
 *
 * The distinction this module exists to keep straight (owner, 2026-08-18: "if a
 * patient ever reached that status the count needs to account for that lead
 * being in that status even if they progress to the next one"):
 *
 *   here    — occupancy. Contacts whose CURRENT lifecycle_status is this stage.
 *             Each contact is in exactly ONE of these, so summing them is the
 *             cohort size.
 *   reached — cumulative. Contacts that EVER got this far. A lead appears under
 *             every stage it passed through, so summing THESE would count one
 *             person seven times — which is exactly the mistake a "total"
 *             derived from the wrong column would make.
 *
 * Both come from crm.funnel_stats (20260818140000), one row per group per
 * status carrying both numbers.
 */
import { FunnelRow } from '../../types';

/** The forward spine, in order. Cumulative and therefore monotonic. */
export const FUNNEL_ORDER = [
  'new', 'contacting', 'qualified_pending_records',
  'ready_to_schedule', 'scheduled', 'screened', 'randomized',
];

/** Terminal exits. Off the spine, so never a subset of anything above. */
export const EXIT_ORDER = ['screen_failed', 'not_eligible', 'not_interested', 'unreachable'];

export interface StageCounts {
  /** Ever reached this stage. */
  reached: number;
  /** Sitting in this stage right now. */
  here: number;
}

export interface GroupFunnel {
  key: string;
  label: string;
  /** Contacts in the cohort — the sum of the OCCUPANCY counts, one per contact. */
  total: number;
  byStatus: Record<string, StageCounts>;
}

/**
 * Fold the flat rows into one funnel per group, biggest cohort first.
 *
 * `reached` missing entirely means the backend predates 20260818140000, which
 * happens for the length of one deploy. Falling back to occupancy keeps the tab
 * correct-if-conservative for those few minutes instead of drawing every bar at
 * zero and looking like a data loss.
 */
export function buildGroups(rows: FunnelRow[], onlyGroupKey: string | null = null): GroupFunnel[] {
  const byKey = new Map<string, GroupFunnel>();
  for (const r of rows) {
    if (onlyGroupKey && r.group_key !== onlyGroupKey) continue;
    const g = byKey.get(r.group_key)
      || { key: r.group_key, label: r.group_label, total: 0, byStatus: {} };
    const here = Number(r.total) || 0;
    const reached = r.reached == null ? here : Number(r.reached) || 0;
    const prev = g.byStatus[r.lifecycle_status] || { reached: 0, here: 0 };
    g.byStatus[r.lifecycle_status] = { reached: prev.reached + reached, here: prev.here + here };
    g.total += here;
    byKey.set(r.group_key, g);
  }
  return [...byKey.values()].sort((a, b) => b.total - a.total);
}

/** A stage's counts, zeroed rather than undefined so callers never branch. */
export function countsAt(g: GroupFunnel, status: string): StageCounts {
  return g.byStatus[status] || { reached: 0, here: 0 };
}

/**
 * Bar scale for the forward spine: the widest reached count, which after the
 * cumulative change is the TOP of the funnel. Every bar below then reads as a
 * share of it, which is what makes the shape a funnel rather than a bar chart.
 */
export function spineMax(g: GroupFunnel): number {
  return Math.max(...FUNNEL_ORDER.map((s) => countsAt(g, s).reached), 1);
}

/**
 * Share of the cohort that ever got this far. Honest in a way the old
 * occupancy share could not be: the spine is cumulative, so this falls
 * monotonically from ~100% and can never exceed it.
 */
export function reachedShare(g: GroupFunnel, status: string): string | null {
  const { reached } = countsAt(g, status);
  if (reached <= 0 || g.total <= 0) return null;
  return `${Math.round((reached / g.total) * 100)}%`;
}
